import { observer } from "mobx-react"
import DataTable from "../../../components/DataTable/DataTable"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { TransactionsViewModel } from "../TransactionsViewModel"
import DataTableRowsSkeleton from "../../../styledComponents/LoadingSkeletons/DataTableRowsSkeleton"
import { GiftCard } from "../../../../domain/entities/GiftCard"
import { Tooltip } from "antd"
import { Translation } from "../../../stores/TranslationStore"
import { Microsite } from "../../../../domain/entities/Microsite"
import { currencyFormatter } from "../../../utils/currencyFormatter"
import { useTranslation } from "react-i18next"

interface GiftCardsTableProps {
	data: GiftCard[]
	loading?: boolean
	isFetching?: boolean
	onChangePage?: (page: number, pageSize: number, lastPageFetched: number) => void
}

const GiftCardsTable = ({ data, loading, isFetching, onChangePage }: GiftCardsTableProps) => {
	const viewModel = useContainerInjection<TransactionsViewModel>("TransactionsViewModel")
	const handlePageChange = (page: number, pageSize: number, lastPageFetched: number) => {
		viewModel.setTableConfig({ pageSize })
		onChangePage && onChangePage(page, pageSize, lastPageFetched)
	}
	const translation = useContainerInjection<Translation>("Translation")
	const { t } = useTranslation("transactions")

	const columns = [
		{
			title: t("giftCardTable.movementType"),
			dataIndex: "movementType",
			key: "movementType",
			width: 150
		},
		{
			title: t("giftCardTable.destinationEmail"),
			dataIndex: "destinationEmail",
			key: "destinationEmail",
			width: 200
		},
		{
			title: t("giftCardTable.senderEmail"),
			dataIndex: "senderEmail",
			key: "senderEmail",
			width: 200
		},
		{
			title: t("giftCardTable.requestServiceId"),
			dataIndex: "requestServiceId",
			key: "requestServiceId",
			width: 150
		},
		{
			title: t("form.dataTableHeaders.date"),
			dataIndex: "date",
			key: "date",
			sorter: (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime(),
			width: 180
		},
		{
			title: t("giftCardTable.amount"),
			dataIndex: "amount",
			key: "amount",
			width: 120
		},
		{
			title: t("giftCardTable.currentBalance"),
			dataIndex: "currentBalance",
			key: "currentBalance",
			width: 120
		},
		{
			title: t("giftCardTable.description"),
			dataIndex: "description",
			key: "description",
			width: 250
		}
	]

	return (
		<DataTable
			loading={loading}
			tableConfig={{
				sort: viewModel.tableConfig.sort,
				pageSize: viewModel.tableConfig.pageSize
			}}
			isFetching={isFetching}
			renderCustomLoadingComponent={() => <DataTableRowsSkeleton size="lg" />}
			onChange={data => {
				data.sorter &&
					viewModel.setTableConfig({ sort: { order: data.sorter.order, field: data.sorter.field } })
			}}
			onChangePage={handlePageChange}
			columns={columns}
			dataSource={data.map((giftCard, idx) => {
				const transactionDate = giftCard.date
					? new Date(giftCard.date.replace("T", " ").replace("Z", ""))
					: new Date()

				// Translate movement type
				const translatedMovementType = giftCard.movementType
					? t(`movementTypes.${giftCard.movementType.toLowerCase()}`)
					: "-"

				return {
					key: giftCard.id,
					movementType: translatedMovementType,
					destinationEmail: giftCard.destinationEmail || "-",
					senderEmail: giftCard.senderEmail || "-",
					requestServiceId: giftCard.requestServiceId || "-",
					date: new Date(
						transactionDate.getFullYear(),
						transactionDate.getMonth(),
						transactionDate.getDate()
					).toLocaleDateString(translation.currentLang, {
						weekday: "short",
						year: "numeric",
						month: "short",
						day: "numeric"
					}),
					amount: currencyFormatter(giftCard.amount, giftCard.currency),
					currentBalance: currencyFormatter(giftCard.currentBalance, giftCard.currency),
					description: giftCard.description || "-"
				}
			})}
		/>
	)
}

export default observer(GiftCardsTable)
